<script setup>
import apiManager from '@/api';
import '@fullcalendar/core/vdom';
import FullCalendar from '@fullcalendar/vue3';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import scrollGrid from '@fullcalendar/scrollgrid';
import {
  reactive, ref, computed, watch,
} from 'vue';
import { isAfter } from '@/utils/date';
import dayjs from '@/utils/dayjs';
import appointmentsToCalendarData from './appointmentsToCalendarData';
import AppointmentDetailsModal from './AppointmentDetailsModal.vue';

const props = defineProps({
  event: Object,
  appointments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['open-session', 'refresh']);

const calendarData = computed(() => appointmentsToCalendarData(props.appointments));

const session = reactive({});

const showDetailsModal = ref(false);

const toggleDetailsModal = () => {
  showDetailsModal.value = !showDetailsModal.value;
  if (!showDetailsModal.value) {
    session.value = {};
  }
};

const calendarBaseOptions = reactive({
  // initialDate: calendarEvents.value[0]?.start,
  slotMinTime: '06:00:00',
  slotMaxTime: '18:00:00',
  contentHeight: 'auto',
  dayMinWidth: '200',
  allDaySlot: false,
  eventColor: 'gray',
  schedulerLicenseKey: '0588850945-fcs-1669066481',
  plugins: [resourceTimeGridPlugin, scrollGrid],
  initialView: 'resourceTimeGridDay',
  displayEventTime: false,
  eventClick: (info) => {
    emit('open-session', info.event.id);
    const getSession = async () => {
      try {
        const { data } = await apiManager.appointments.getSession(info.event.id);
        const date = {
          start: info.event.start,
          end: info.event.end,
        };
        session.value = Object.assign(date, data);
        toggleDetailsModal();
      } catch (e) {
        console.log(e);
      }
    };
    getSession();
  },
});

const calendarOptions = computed(() => ({
  ...calendarBaseOptions,
  resources: calendarData.value?.resources,
  events: calendarData.value?.events,
}));

const calendar = ref(undefined);
watch(() => calendarOptions.value.events, () => {
  const currentAndFutureEvents = calendarOptions.value.events.filter((e) => isAfter(dayjs().startOf('day'))(e.start));
  const nearestEvent = currentAndFutureEvents.reduce(
    (acc, e) => (!acc || e.start < acc?.start ? e : acc),
    undefined,
  );
  const calendarAPI = calendar.value.getApi();
  if (nearestEvent?.start) calendarAPI.gotoDate(nearestEvent?.start);
});

</script>
<template>
  <appointment-details-modal
    v-if="showDetailsModal"
    :eventId="props.event.id"
    :session="session.value"
    @close="toggleDetailsModal"
    @refresh="emit('refresh')"
  />
  <div class="p-[2rem]">
    <h1 class="
      font-FordAntennaSemibold tablet:font-FordAntennaMed
      text-[18px] tablet:text-[34px]
      mb-[2rem]
    ">Appointment Calendar <span v-html="event?.name"></span></h1>
    <full-calendar ref="calendar" :options="calendarOptions" />
  </div>
</template>
